import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Camera, Plus, Trash2, FileText, CheckCircle2 } from 'lucide-react';

export default function HandoverWizard({ onSuccess }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        protocol_type: "Check-in",
        tenant_name: "",
        tenant_email: "",
        address: "Gateway Studios, Fornebu",
        electricity_meter: "",
        water_meter: "",
        keys_handed_over: 0,
        keys_returned: 0,
        key_comment: "",
        notes: "",
        defects: [] // {room, description, photo_base64}
    });
    const [submitting, setSubmitting] = useState(false);

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // --- Defect & Photo Logic ---
    const addDefect = () => {
        setFormData(prev => ({
            ...prev,
            defects: [...prev.defects, { room: "", description: "", photo_base64: null }]
        }));
    };

    const removeDefect = (index) => {
        setFormData(prev => ({
            ...prev,
            defects: prev.defects.filter((_, i) => i !== index)
        }));
    };

    const updateDefect = (index, field, value) => {
        const newDefects = [...formData.defects];
        newDefects[index][field] = value;
        setFormData(prev => ({ ...prev, defects: newDefects }));
    };

    const handlePhotoUpload = (e, index) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateDefect(index, 'photo_base64', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const [lastSubmissionId, setLastSubmissionId] = useState(null);

    // --- Submission ---
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const response = await fetch('http://localhost:3001/api/handover/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            
            if (!response.ok) throw new Error('Failed to submit');
            
            const data = await response.json();
            setLastSubmissionId(data.id);
            setStep(4); // Go to success step
        } catch (e) {
            console.error(e);
            alert("Error saving protocol");
        } finally {
            setSubmitting(false);
        }
    };

    // --- Render Steps ---
    const renderStep1 = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Type</Label>
                    <select
                        className="w-full p-2 border rounded-md"
                        value={formData.protocol_type}
                        onChange={(e) => updateField('protocol_type', e.target.value)}
                    >
                        <option>Check-in</option>
                        <option>Check-out</option>
                    </select>
                </div>
                <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" disabled value={new Date().toISOString().split('T')[0]} />
                </div>
            </div>

            <div className="space-y-2">
                <Label>{t('tenant_name')}</Label>
                <Input value={formData.tenant_name} onChange={e => updateField('tenant_name', e.target.value)} placeholder="Enter name..." />
            </div>
            <div className="space-y-2">
                <Label>{t('tenant_email')}</Label>
                <Input type="email" value={formData.tenant_email} onChange={e => updateField('tenant_email', e.target.value)} placeholder="tenant@example.com" />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>{t('keys_given')}</Label>
                    <Input type="number" value={formData.keys_handed_over} onChange={e => updateField('keys_handed_over', e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label>{t('keys_returned')}</Label>
                    <Input type="number" value={formData.keys_returned} onChange={e => updateField('keys_returned', e.target.value)} />
                </div>
            </div>

            <Button className="w-full mt-4" onClick={() => setStep(2)}>{t('next')}: Inspection</Button>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label>{t('electricity_meter')}</Label>
                <Input value={formData.electricity_meter} onChange={e => updateField('electricity_meter', e.target.value)} placeholder="kWh" />
            </div>

            <div className="border-t pt-4">
                <Label className="text-lg font-bold mb-2 block">{t('room_inspection')}</Label>
                <p className="text-xs text-gray-500 mb-4">Take photos of any damages or specific conditions.</p>

                {formData.defects.map((defect, idx) => (
                    <Card key={idx} className="mb-4">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex justify-between items-start">
                                <Label className="font-bold">Item #{idx + 1}</Label>
                                <Button variant="ghost" size="sm" onClick={() => removeDefect(idx)}>
                                    <Trash2 className="text-red-500 w-4 h-4" />
                                </Button>
                            </div>

                            <Input
                                placeholder="Room / Area (e.g. Kitchen)"
                                value={defect.room}
                                onChange={e => updateDefect(idx, 'room', e.target.value)}
                            />
                            <Textarea
                                placeholder="Description of condition..."
                                value={defect.description}
                                onChange={e => updateDefect(idx, 'description', e.target.value)}
                            />

                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        id={`photo-${idx}`}
                                        className="hidden"
                                        onChange={(e) => handlePhotoUpload(e, idx)}
                                    />
                                    <Label htmlFor={`photo-${idx}`} className="cursor-pointer flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md border">
                                        <Camera className="w-5 h-5 text-gray-700" />
                                        <span className="text-sm font-medium">{t('take_photo')}</span>
                                    </Label>
                                </div>
                                {defect.photo_base64 && (
                                    <img src={defect.photo_base64} alt="Preview" className="w-12 h-12 object-cover rounded-md border" />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}

                <Button variant="outline" className="w-full border-dashed" onClick={addDefect}>
                    <Plus className="w-4 h-4 mr-2" /> {t('add_observation')}
                </Button>
            </div>

            <div className="flex gap-4 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>{t('back')}</Button>
                <Button className="flex-1" onClick={() => setStep(3)}>{t('next')}: Review</Button>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-bold">Summary</h3>
            <div className="text-sm space-y-2 bg-gray-50 p-4 rounded-md">
                <p><b>Tenant:</b> {formData.tenant_name}</p>
                <p><b>Protocol:</b> {formData.protocol_type}</p>
                <p><b>Keys:</b> {formData.keys_handed_over} out / {formData.keys_returned} in</p>
                <p><b>Observations:</b> {formData.defects.length} recorded</p>
            </div>

            <div className="space-y-2">
                <Label>Electronic Signature</Label>
                <div className="h-20 bg-gray-100 border border-dashed rounded-md flex items-center justify-center text-gray-400">
                    [Signature Placeholder]
                </div>
                <p className="text-xs text-gray-400 text-center">By clicking Submit, you certify this information.</p>
            </div>

            <div className="flex gap-4 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>{t('back')}</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? t('sending') : "Sign & Send PDF"}
                </Button>
            </div>
        </div>
    );

    // --- Success View ---
    const renderSuccess = () => {
        const pdfUrl = `http://localhost:3001/api/handover/${lastSubmissionId}/pdf`;
        const mailtoLink = `mailto:${formData.tenant_email}?subject=Overtakelsesprotokoll - ${formData.address}&body=Hei ${formData.tenant_name},%0D%0A%0D%0AVennligst finn vedlagt overtakelsesprotokoll.%0D%0A%0D%0AMvh,%0D%0AGateway Studios`;

        return (
            <div className="space-y-6 text-center py-10">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold">{t('protocol_saved')}</h2>
                <p className="text-gray-500">The protocol has been generated successfully.</p>

                <div className="space-y-3 max-w-xs mx-auto">
                    <p className="text-sm font-bold text-gray-700">Generate Protocol:</p>
                    <a href={pdfUrl} download target="_blank" rel="noreferrer" className="block w-full">
                        <Button variant="outline" className="w-full h-12">
                            1. {t('download_pdf')}
                        </Button>
                    </a>

                    <a href={mailtoLink} className="block w-full">
                        <Button className="w-full h-12">
                            2. {t('send_email')}
                        </Button>
                    </a>
                </div>

                <Button variant="ghost" className="mt-8" onClick={() => {
                    setStep(1);
                    setFormData({
                        protocol_type: "Check-in",
                        tenant_name: "",
                        tenant_email: "",
                        address: "Gateway Studios, Fornebu",
                        electricity_meter: "",
                        water_meter: "",
                        keys_handed_over: 0,
                        keys_returned: 0,
                        key_comment: "",
                        notes: "",
                        defects: []
                    });
                    if (onSuccess) onSuccess();
                }}>
                    {t('start_new_protocol')}
                </Button>
            </div>
        );
    };

    return (
        <div className="max-w-xl mx-auto pb-10">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6" /> {t('handover_protocol')}
            </h2>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderSuccess()}
        </div>
    );
}